import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, unref, type MaybeRef } from 'vue';
import { env } from "@/utils/env";
import { useSession } from "@/modules/auth/hooks/useSession";
import { urlEncode } from "@/utils/url";
import type { IProviderImport, ProviderImportRequest } from 'findef';

export const BASE_URL: string = env.VITE_APP_SERVICE_IR;

export type FindexQueryParams = {
  payload?: any;
  urlParams?: Record<string, any>;
  headers?: Record<string, string>;
  invalidates?: Array<string[]>;
  deps?: any[];
}


export const useFindexQuery = () => {
  const session = useSession();
  const baseUrl = BASE_URL + '';


  const requestHeaders = computed((): Record<string, string> => {
    return {
      Authorization: 'Bearer ' + session.state.token,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(session.state.findexSessionId ? {
        'X-findexSessionId': session.state.findexSessionId,
        'access-control-expose-headers': 'X-findexSessionId'
      } : {})
    }
  });

  const urlFor = (endpoint: string, urlParams?: Record<string, any>) => {
    let url = (
      `${baseUrl}` +
      (endpoint.startsWith('/') || baseUrl.endsWith('/') ? endpoint : `/${endpoint}`)
    );


    if (urlParams) {
      const encoded = urlEncode(urlParams);
      url = `${url}?${encoded}`;
    }

    return url;
  }

  const extractBody = (args: FindexQueryParams) => {
    if (!args.payload) return undefined;
    if (typeof args.payload === 'string') return args.payload;
    return JSON.stringify(args.payload);
  }

  const request = async <T = any>(endpoint: string, method: string, args: FindexQueryParams = {}): Promise<T> => {
    const url = urlFor(endpoint, args.urlParams);
    const headers = {
      ...requestHeaders.value,
      ...(args.headers || {})
    }
    const resp = await fetch(url, {
      method: method,
      headers: headers,
      ...((method === 'GET' || method === 'DELETE') ? {} : {
        body: extractBody(args)
      })
    });
    const data = await resp.json();
    return data as T;
  }

  const get = async <T = any>(endpoint: string, args: FindexQueryParams = {}): Promise<T> => {
    return await request<T>(endpoint, 'GET', args);
  }

  const post = async <T = any>(endpoint: string, args: FindexQueryParams = {}): Promise<T> => {
    return await request<T>(endpoint, 'POST', args);
  }

  const put = async <T = any>(endpoint: string, args: FindexQueryParams = {}): Promise<T> => {
    return await request<T>(endpoint, 'PUT', args);
  }

  const patch = async <T = any>(endpoint: string, args: FindexQueryParams = {}): Promise<T> => {
    return await request<T>(endpoint, 'PATCH', args);
  }

  const del = async <T = any>(endpoint: string, args: FindexQueryParams = {}): Promise<T> => {
    return await request<T>(endpoint, 'DELETE', args);
  }

  const client = useQueryClient();

  const useGet = <T = any>(endpoint: string, args: FindexQueryParams = {}) => {
    const url = urlFor(endpoint, args.urlParams);
    return useQuery({
      queryKey: [endpoint, url, requestHeaders],
      queryFn: async () => {
        return await get<T>(endpoint, args);
      },
    });
  }

  /* const usePost = <T = any>(endpoint: string, args: FindexQueryParams = {}) => {
    const url = urlFor(endpoint, args.urlParams);
    return useMutation<T, Error, FindexQueryParams | undefined | void>({
      mutationKey: [url, requestHeaders, ...(args.deps || [])],
      mutationFn: async (options) => {
        return await post<T>(endpoint, {...args, ...(options || {})});
      },
      onSuccess: () => {
        if (args.invalidates) {
          args.invalidates.forEach((keys) => {
            client.invalidateQueries({ queryKey: keys })
          })
        }
      }
    });
  } */

  return {
    queries: {
      getProviderImports: () => useGet<IProviderImport[]>('/finsquid/import', {}),
      useGetProviderImport: (providerId: MaybeRef<number>, options: { enabled?: MaybeRef<boolean> } = {}) => {
        return useQuery({
          queryKey: ['/finsquid/import', providerId, requestHeaders],
          queryFn: async () => {
            const providerIdNumber = unref(providerId);
            return await get<IProviderImport>(`/finsquid/import/${providerIdNumber}`);
          },
          enabled: computed(() => {
            const providerIdNumber = unref(providerId);
            const hasValidProvider = providerIdNumber >= 0;
            const extraEnabled =
              typeof options.enabled === 'undefined' ? true : unref(options.enabled);
            return hasValidProvider && extraEnabled;
          }),
        });
      }
    },
    mutations: {
      useUpsertProviderImport: () => {
        const endpoint = '/finsquid/import';
        const url = urlFor(endpoint);
        return useMutation<IProviderImport, Error, ProviderImportRequest>({
          mutationKey: [url, requestHeaders],
          mutationFn: async (providerImport) => {
            return await post<IProviderImport>(endpoint, { payload: {...providerImport, doNotRun: true } });
          },
          onSuccess: () => {
            client.invalidateQueries({ queryKey: [endpoint] })
          }
        })
      },
      useRunProviderImport: () => {
        const endpoint = '/finsquid/import';
        const url = urlFor(endpoint);
        return useMutation<IProviderImport, Error, ProviderImportRequest>({
          mutationKey: [url, requestHeaders],
          mutationFn: async (providerImport) => {
            return await post<IProviderImport>(endpoint, { payload: providerImport });
          },
          onSuccess: () => {
            client.invalidateQueries({ queryKey: [endpoint] })
          }
        })
      }
    },
    methods: {
      get,
      post,
      put,
      patch,
      del
    },
    client
  }
}
